import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    FormControl,
    FormLabel,
    HStack,
    Icon,
    IconButton,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Spinner,
    Tag,
    TagCloseButton,
    TagLabel,
    Text,
    Tr,
    VStack,
    Wrap,
    WrapItem,
} from '@chakra-ui/react';
import { TbPlus } from 'react-icons/tb';
import type { SupplierVendorNexAliasGroup } from '../../hooks/useGetSettings';
import AddSupplierVendorMappingModal from '../../modals/settings/AddSupplierVendorMappingModal';
import PartBayButton from '../PartBayButton';
import { sortVendorNexAliasMappingsBySupplier } from '../../utils/vendorNexAliasMappingsUtils';
import {
    SettingsCard,
    SettingsRowActions,
    SettingsSubTable,
    SettingsTd,
    SettingsTh,
    useSubTablePagination,
} from './primitives';

type AliasTagKind = 'invoice' | 'nex';

type AddAliasModalState = {
    supplierId: string;
    kind: AliasTagKind;
};

/**
 * An alias, as one line of text. Both columns hold the same kind of thing — a
 * name someone typed — so they read as the same neutral chip rather than being
 * colour-coded by column.
 */
function MappingAliasTag({
    label,
    onRemove,
    isDisabled,
}: {
    label: string;
    onRemove: () => void;
    isDisabled?: boolean;
}) {
    return (
        <WrapItem>
            <Tag
                size="sm"
                borderRadius="md"
                variant="outline"
                border="1px solid"
                borderColor="gray.200"
                bg="gray.50"
                px={2}
                py={0.5}
                boxShadow="none"
                _hover={{ borderColor: 'gray.300', bg: 'white' }}
            >
                <TagLabel fontSize="xs" color="gray.700" maxW="220px" isTruncated>
                    {label}
                </TagLabel>
                <TagCloseButton
                    color="gray.400"
                    _hover={{ color: 'gray.600' }}
                    onClick={onRemove}
                    isDisabled={isDisabled}
                    ml={1}
                    boxSize="14px"
                />
            </Tag>
        </WrapItem>
    );
}

function AddAliasTagButton({
    onClick,
    isDisabled,
    ariaLabel,
}: {
    onClick: () => void;
    isDisabled?: boolean;
    ariaLabel: string;
}) {
    return (
        <WrapItem>
            <IconButton
                aria-label={ariaLabel}
                icon={<Icon as={TbPlus as React.ElementType} boxSize={3} />}
                size="xs"
                variant="outline"
                borderRadius="md"
                border="1px dashed"
                borderColor="gray.300"
                bg="white"
                color="gray.500"
                h="22px"
                minW="22px"
                _hover={{ bg: 'gray.50', borderColor: 'gray.400', color: 'gray.700' }}
                onClick={onClick}
                isDisabled={isDisabled}
            />
        </WrapItem>
    );
}

function AliasTagList({
    tags,
    onChange,
    onRequestAdd,
    isDisabled,
    addLabel,
}: {
    tags: string[];
    onChange: (next: string[]) => void;
    onRequestAdd: () => void;
    isDisabled?: boolean;
    addLabel: string;
}) {
    return (
        <Wrap spacing={1.5} align="center">
            {tags.map((tag, tagIndex) => (
                <MappingAliasTag
                    key={`${tag}-${tagIndex}`}
                    label={tag}
                    isDisabled={isDisabled}
                    onRemove={() => onChange(tags.filter((_, i) => i !== tagIndex))}
                />
            ))}
            <AddAliasTagButton
                onClick={onRequestAdd}
                isDisabled={isDisabled}
                ariaLabel={addLabel}
            />
        </Wrap>
    );
}

export interface VendorNexAliasMappingsSectionProps {
    mappings: SupplierVendorNexAliasGroup[];
    onMappingsChange: (next: SupplierVendorNexAliasGroup[]) => void;
    isSaving: boolean;
}

export default function VendorNexAliasMappingsSection({
    mappings,
    onMappingsChange,
    isSaving,
}: VendorNexAliasMappingsSectionProps) {
    const [isAddSupplierOpen, setIsAddSupplierOpen] = useState(false);
    const [addAliasModal, setAddAliasModal] = useState<AddAliasModalState | null>(null);
    const [aliasModalDraft, setAliasModalDraft] = useState('');

    const sortedMappings = useMemo(
        () => sortVendorNexAliasMappingsBySupplier(mappings),
        [mappings]
    );

    // Ten suppliers a page, like every other settings table.
    const paging = useSubTablePagination(sortedMappings);

    const existingSupplierIds = useMemo(() => mappings.map((m) => m.supplierId), [mappings]);

    const addModalGroup =
        addAliasModal != null
            ? mappings.find((g) => g.supplierId === addAliasModal.supplierId) ?? null
            : null;

    useEffect(() => {
        if (!addAliasModal) {
            setAliasModalDraft('');
        }
    }, [addAliasModal]);

    const closeAddAliasModal = () => {
        setAddAliasModal(null);
        setAliasModalDraft('');
    };

    const updateGroup = (supplierId: string, patch: Partial<SupplierVendorNexAliasGroup>) => {
        onMappingsChange(mappings.map((g) => (g.supplierId === supplierId ? { ...g, ...patch } : g)));
    };

    const removeGroup = (supplierId: string) => {
        onMappingsChange(mappings.filter((g) => g.supplierId !== supplierId));
    };

    const handleAddSupplier = (group: SupplierVendorNexAliasGroup) => {
        if (existingSupplierIds.includes(group.supplierId)) return;
        onMappingsChange([...mappings, group]);
    };

    const confirmAddAlias = () => {
        if (!addAliasModal || !addModalGroup) return;
        const value = aliasModalDraft.trim();
        if (!value) return;

        const { supplierId, kind } = addAliasModal;
        const group = mappings.find((g) => g.supplierId === supplierId);
        if (!group) return;

        if (kind === 'invoice') {
            const exists = group.invoiceTextAliases.some(
                (t) => t.trim().toLowerCase() === value.toLowerCase()
            );
            if (!exists) {
                updateGroup(supplierId, {
                    invoiceTextAliases: [...group.invoiceTextAliases, value],
                });
            }
        } else {
            const names = group.nexVendorMappings.map((m) => m.vendorName);
            const exists = names.some((t) => t.trim().toLowerCase() === value.toLowerCase());
            if (!exists) {
                updateGroup(supplierId, {
                    nexVendorMappings: [...group.nexVendorMappings, { vendorName: value }],
                });
            }
        }
        closeAddAliasModal();
    };

    const addModalTitle =
        addAliasModal?.kind === 'invoice' ? 'Add Text on Invoice' : 'Add Nexsyis Vendor Name';
    const addModalLabel =
        addAliasModal?.kind === 'invoice'
            ? 'Text as it appears on the invoice'
            : 'Vendor name as shown on the Nexsyis PO';

    return (
        <>
            <SettingsCard
                title="Invoice Vendor Alias"
                description="Add aliases for suppliers when the name that appears on an invoice is different from the vendor name Nexsyis has saved. You can build these up over time to increase automation."
                headerAction={
                    <>
                        {isSaving ? (
                            <HStack spacing={2} color="gray.500">
                                <Spinner size="sm" />
                                <Text fontSize="sm">Saving…</Text>
                            </HStack>
                        ) : null}
                        <PartBayButton
                            colorScheme="partbay-blue"
                            variant="solid"
                            size="sm"
                            text="Add Supplier"
                            onClick={() => setIsAddSupplierOpen(true)}
                            isDisabled={isSaving}
                        />
                    </>
                }
                dividers={false}
            >
                {/* py matches SettingsRow so the table lines up with the rows
                    of the cards above it. */}
                <Box py={4}>
                    <SettingsSubTable
                        isInset
                        currentPage={paging.currentPage}
                        totalPages={paging.totalPages}
                        onPageChange={paging.setCurrentPage}
                        head={
                            <>
                                <SettingsTh w="200px">Supplier</SettingsTh>
                                <SettingsTh>Text on invoice</SettingsTh>
                                <SettingsTh>
                                    {/* SettingsTh uppercases its text; opt the brand
                                        name out so it keeps its own casing. */}
                                    <Text as="span" textTransform="none">
                                        Nexsyis
                                    </Text>{' '}
                                    vendor names
                                </SettingsTh>
                                <SettingsTh textAlign="center" w="100px">
                                    Actions
                                </SettingsTh>
                            </>
                        }
                    >
                        {sortedMappings.length === 0 ? (
                            <Tr>
                                <SettingsTd colSpan={4} py={6} color="gray.500">
                                    No supplier aliases yet. Add a supplier to configure aliases.
                                </SettingsTd>
                            </Tr>
                        ) : (
                            paging.pageItems.map((group) => (
                                <Tr key={group.supplierId}>
                                    <SettingsTd verticalAlign="top" fontWeight="medium" color="gray.800">
                                        {group.supplierTitle}
                                    </SettingsTd>
                                    <SettingsTd verticalAlign="top">
                                        <AliasTagList
                                            tags={group.invoiceTextAliases}
                                            isDisabled={isSaving}
                                            addLabel={`Add invoice text for ${group.supplierTitle}`}
                                            onRequestAdd={() =>
                                                setAddAliasModal({
                                                    supplierId: group.supplierId,
                                                    kind: 'invoice',
                                                })
                                            }
                                            onChange={(invoiceTextAliases) =>
                                                updateGroup(group.supplierId, { invoiceTextAliases })
                                            }
                                        />
                                    </SettingsTd>
                                    <SettingsTd verticalAlign="top">
                                        <AliasTagList
                                            tags={group.nexVendorMappings.map((m) => m.vendorName)}
                                            isDisabled={isSaving}
                                            addLabel={`Add Nexsyis vendor name for ${group.supplierTitle}`}
                                            onRequestAdd={() =>
                                                setAddAliasModal({
                                                    supplierId: group.supplierId,
                                                    kind: 'nex',
                                                })
                                            }
                                            onChange={(names) =>
                                                updateGroup(group.supplierId, {
                                                    nexVendorMappings: names.map((vendorName) => ({
                                                        vendorName,
                                                    })),
                                                })
                                            }
                                        />
                                    </SettingsTd>
                                    <SettingsTd verticalAlign="top" textAlign="center">
                                        <SettingsRowActions
                                            onDelete={() => removeGroup(group.supplierId)}
                                            deleteLabel={`Remove ${group.supplierTitle} aliases`}
                                            isDeleteDisabled={isSaving}
                                        />
                                    </SettingsTd>
                                </Tr>
                            ))
                        )}
                    </SettingsSubTable>
                </Box>
            </SettingsCard>

            <Modal isOpen={addAliasModal != null} onClose={closeAddAliasModal} isCentered size="md">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader fontSize="md" fontWeight="semibold" color="gray.800" py={4}>
                        {addModalTitle}
                    </ModalHeader>
                    <ModalCloseButton top={3} />
                    <ModalBody>
                        <VStack align="stretch" spacing={3}>
                            {addModalGroup ? (
                                <Text fontSize="sm" color="gray.600">
                                    Supplier: <strong>{addModalGroup.supplierTitle}</strong>
                                </Text>
                            ) : null}
                            <FormControl>
                                <FormLabel fontSize="sm">{addModalLabel}</FormLabel>
                                <Input
                                    size="sm"
                                    value={aliasModalDraft}
                                    autoFocus
                                    placeholder={addModalLabel}
                                    isDisabled={isSaving}
                                    onChange={(e) => setAliasModalDraft(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            confirmAddAlias();
                                        }
                                    }}
                                />
                            </FormControl>
                        </VStack>
                    </ModalBody>
                    <ModalFooter>
                        <Button size="action" variant="outlineGray" mr={3} onClick={closeAddAliasModal} isDisabled={isSaving}>
                            Cancel
                        </Button>
                        <Button
                            size="action"
                            colorScheme="partbay-blue"
                            onClick={confirmAddAlias}
                            isDisabled={!aliasModalDraft.trim() || isSaving}
                            isLoading={isSaving}
                        >
                            Add
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <AddSupplierVendorMappingModal
                isOpen={isAddSupplierOpen}
                onClose={() => setIsAddSupplierOpen(false)}
                excludedSupplierIds={existingSupplierIds}
                onAdd={handleAddSupplier}
                isDisabled={isSaving}
            />
        </>
    );
}
